import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { instances } from '@/services/http';
import { clearJwtTokens } from '@/services/auth-tokens';
import type {
  // New API Types - GET Endpoints
  SignUpAmbassadorGetPublicFromCampaignResponseType,
  SignUpAmbassadorGetProtectedFromCampaignResponseType,
  SignUpAmbassadorGetPublicFromTeamspaceResponseType,
  SignUpAmbassadorGetProtectedFromTeamspaceResponseType,

  // New API Types - PUT/POST Endpoints
  SignUpAmbassadorUpdateLanguageRequestType,
  SignUpAmbassadorUpdateLanguageResponseType,
  SignUpAmbassadorRegisterHelperByEmailRequestType,
  SignUpAmbassadorRegisterHelperByEmailResponseType,
  SignUpAmbassadorManagePhoneNumberRequestType,
  SignUpAmbassadorManagePhoneNumberResponseType,
  SignUpAmbassadorVerifyPhoneNumberCodeRequestType,
  SignUpAmbassadorVerifyPhoneNumberCodeResponseType,
  SignUpAmbassadorUpdateNameRequestType,
  SignUpAmbassadorUpdateNameResponseType,
  SignUpAmbassadorUpdateProfilePictureRequestType,
  SignUpAmbassadorUpdateProfilePictureResponseType,
  SignUpAmbassadorUpdatePositionLanguagesRequestType,
  SignUpAmbassadorUpdatePositionLanguagesResponseType,
  SignUpAmbassadorUpdateAddressRequestType,
  SignUpAmbassadorUpdateAddressResponseType,
  SignUpAmbassadorUpdateActionsInterestedInRequestType,
  SignUpAmbassadorUpdateActionsInterestedInResponseType,
  SignUpAmbassadorFillTouchPointsRequestType,
  SignUpAmbassadorFillTouchPointsResponseType,
} from '@magma-app/magma-types/sign-up-ambassador';

type RegisterHelperByEmailMutationInput =
  SignUpAmbassadorRegisterHelperByEmailRequestType & {
    turnstileToken?: string;
  };

/**
 * Composable for managing ambassador signup API operations with TanStack Query
 * Provides queries, mutations, and caching for all ambassador signup endpoints
 */
export function useAmbassadorSignupApi() {
  const queryClient = useQueryClient();

  // ===== QUERY KEYS =====
  const queryKeys = {
    publicCampaign: (campaignUuid: string) =>
      ['ambassador-signup', 'campaign', 'public', campaignUuid] as const,
    protectedCampaign: (campaignUuid: string) =>
      ['ambassador-signup', 'campaign', 'protected', campaignUuid] as const,
    publicTeamspace: (teamspaceUuid: string) =>
      ['ambassador-signup', 'teamspace', 'public', teamspaceUuid] as const,
    protectedTeamspace: (teamspaceUuid: string) =>
      ['ambassador-signup', 'teamspace', 'protected', teamspaceUuid] as const,
    userProfile: () => ['ambassador-signup', 'user', 'profile'] as const,
  };

  // ===== NEW API ENDPOINTS - GET OPERATIONS =====

  /**
   * GET /sign-up-ambassador/campaign/:campaignUuid
   * Fetch public campaign data for sign-up
   */
  function usePublicCampaignQuery(campaignUuid: string, enabled = true) {
    return useQuery({
      queryKey: queryKeys.publicCampaign(campaignUuid),
      queryFn:
        async (): Promise<SignUpAmbassadorGetPublicFromCampaignResponseType> => {
          const response = await instances.main.get(
            `/sign-up-ambassador/campaign/${campaignUuid}/public`
          );
          return response.data;
        },
      enabled: enabled && !!campaignUuid,
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  }

  /**
   * GET /sign-up-ambassador/campaign/:campaignUuid
   * Fetch protected (authenticated) campaign data for sign-up
   */
  function useProtectedCampaignQuery(campaignUuid: string, enabled = true) {
    return useQuery({
      queryKey: queryKeys.protectedCampaign(campaignUuid),
      queryFn:
        async (): Promise<SignUpAmbassadorGetProtectedFromCampaignResponseType> => {
          const response = await instances.main.get(
            `/sign-up-ambassador/campaign/${campaignUuid}`
          );
          return response.data;
        },
      enabled: enabled && !!campaignUuid,
      staleTime: 1000 * 60 * 2, // 2 minutes (shorter for user data)
      retry: (failureCount, error) => {
        // Don't retry if it's an authorization error that requires disconnection
        if (shouldDisconnectUser(error)) {
          disconnectUserAndReload();
          return false;
        }
        // Default retry behavior for other errors
        return failureCount < 3;
      },
    });
  }

  /**
   * GET /sign-up-ambassador/teamspace/:teamspaceUuid
   * Fetch public teamspace data for sign-up
   */
  function usePublicTeamspaceQuery(teamspaceUuid: string, enabled = true) {
    return useQuery({
      queryKey: queryKeys.publicTeamspace(teamspaceUuid),
      queryFn:
        async (): Promise<SignUpAmbassadorGetPublicFromTeamspaceResponseType> => {
          const response = await instances.main.get(
            `/sign-up-ambassador/teamspace/${teamspaceUuid}/public`
          );
          return response.data;
        },
      enabled: enabled && !!teamspaceUuid,
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  }

  /**
   * GET /sign-up-ambassador/teamspace/:teamspaceUuid
   * Fetch protected (authenticated) teamspace data for sign-up
   */
  function useProtectedTeamspaceQuery(teamspaceUuid: string, enabled = true) {
    return useQuery({
      queryKey: queryKeys.protectedTeamspace(teamspaceUuid),
      queryFn:
        async (): Promise<SignUpAmbassadorGetProtectedFromTeamspaceResponseType> => {
          const response = await instances.main.get(
            `/sign-up-ambassador/teamspace/${teamspaceUuid}`
          );

          return response.data;
        },
      enabled: enabled && !!teamspaceUuid,
      staleTime: 1000 * 60 * 2, // 2 minutes (shorter for user data)
      retry: (failureCount, error) => {
        // Don't retry if it's an authorization error that requires disconnection
        if (shouldDisconnectUser(error)) {
          disconnectUserAndReload();
        }
        // Default retry behavior for other errors
        return false;
      },
    });
  }

  // ===== NEW API ENDPOINTS - PUT/POST OPERATIONS =====

  /**
   * PUT /sign-up-ambassador/language
   * Update user's preferred language
   */
  function useUpdateLanguageMutation() {
    return useMutation<
      SignUpAmbassadorUpdateLanguageResponseType,
      Error,
      SignUpAmbassadorUpdateLanguageRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.put(
          '/sign-up-ambassador/language',
          data
        );
        return response.data;
      },
      onSuccess: () => {
        // Invalidate related queries to refresh user data
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * POST /sign-up-ambassador/email
   * Register a new helper by email
   */
  function useRegisterHelperByEmailMutation() {
    return useMutation<
      SignUpAmbassadorRegisterHelperByEmailResponseType,
      Error,
      RegisterHelperByEmailMutationInput
    >({
      mutationFn: async (data) => {
        const { turnstileToken, ...requestPayload } = data;
        const response = await instances.main.post(
          '/sign-up-ambassador/email',
          requestPayload,
          {
            ...(turnstileToken && {
              headers: {
                'x-turnstile-token': turnstileToken,
              },
            }),
          }
        );
        return response.data;
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/phone
   * Add or update phone number and send verification code
   */
  function useManagePhoneNumberMutation() {
    return useMutation<
      SignUpAmbassadorManagePhoneNumberResponseType,
      Error,
      SignUpAmbassadorManagePhoneNumberRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.put(
          '/sign-up-ambassador/phone',
          data
        );
        return response.data;
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/phone/verify-code
   * Verify phone number with code
   */
  function useVerifyPhoneNumberCodeMutation() {
    return useMutation<
      SignUpAmbassadorVerifyPhoneNumberCodeResponseType,
      Error,
      SignUpAmbassadorVerifyPhoneNumberCodeRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.post(
          '/sign-up-ambassador/phone/verify-code',
          data
        );
        return response.data;
      },
      onSuccess: () => {
        // Invalidate queries to refresh user verification status
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/name
   * Update user's first and last name
   */
  function useUpdateNameMutation() {
    return useMutation<
      SignUpAmbassadorUpdateNameResponseType,
      Error,
      SignUpAmbassadorUpdateNameRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.put(
          '/sign-up-ambassador/name',
          data
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/profile-picture
   * Update user's profile picture
   */
  function useUpdateProfilePictureMutation() {
    return useMutation<
      SignUpAmbassadorUpdateProfilePictureResponseType,
      Error,
      SignUpAmbassadorUpdateProfilePictureRequestType
    >({
      mutationFn: async (data) => {
        const formData = new FormData();
        formData.append('profilePicture', data.profilePicture);

        const response = await instances.main.put(
          '/sign-up-ambassador/profile-picture',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/position-languages
   * Update user's position and spoken languages
   */
  function useUpdatePositionLanguagesMutation() {
    return useMutation<
      SignUpAmbassadorUpdatePositionLanguagesResponseType,
      Error,
      SignUpAmbassadorUpdatePositionLanguagesRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.put(
          '/sign-up-ambassador/position-languages',
          data
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/address
   * Update user's address information
   */
  function useUpdateAddressMutation() {
    return useMutation<
      SignUpAmbassadorUpdateAddressResponseType,
      Error,
      SignUpAmbassadorUpdateAddressRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.put(
          '/sign-up-ambassador/address',
          data
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * PUT /sign-up-ambassador/actionsInterestedIn
   * Update user's interests in different action types
   */
  function useUpdateActionsInterestedInMutation() {
    return useMutation<
      SignUpAmbassadorUpdateActionsInterestedInResponseType,
      Error,
      SignUpAmbassadorUpdateActionsInterestedInRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.put(
          '/sign-up-ambassador/actionsInterestedIn',
          data
        );
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.userProfile() });
      },
    });
  }

  /**
   * POST /sign-up-ambassador/touch-points
   * Complete ambassador-signup by filling touch points and notification preferences
   */
  function useFillTouchPointsMutation() {
    return useMutation<
      SignUpAmbassadorFillTouchPointsResponseType,
      Error,
      SignUpAmbassadorFillTouchPointsRequestType
    >({
      mutationFn: async (data) => {
        const response = await instances.main.post(
          '/sign-up-ambassador/touch-points',
          data
        );
        return response.data;
      },
      // onSuccess: () => {
      //   // Clear all ambassador-signup queries on successful completion
      //   queryClient.invalidateQueries({ queryKey: ['ambassador-signup'] });
      // }
    });
  }

  // ===== UTILITY FUNCTIONS =====

  /**
   * Disconnect user by removing authentication tokens and reloading the page
   * This will force the app to switch from protected to public mode
   */
  function disconnectUserAndReload() {
    clearJwtTokens();
    localStorage.removeItem('auth');
    localStorage.removeItem('user');
    // Clear user data from stores (optional, but good practice)
    queryClient.clear();
    // Reload the page to switch to public mode
    window.location.reload();
  }

  /**
   * Check if error is an authorization error that requires disconnection
   */
  function shouldDisconnectUser(error: unknown): boolean {
    if (!error || typeof error !== 'object' || !('response' in error))
      return false;

    const errorResponse = (
      error as { response?: { data?: { type?: string; subtype?: string } } }
    ).response;
    if (!errorResponse?.data) return false;

    const errorData = errorResponse.data;
    const isAuthError =
      (errorData.type ===
        'ERROR-GET-AMBASSADOR-SIGN-UP-BY-CAMPAIGN-PROTECTED' ||
        errorData.type ===
          'ERROR-GET-AMBASSADOR-SIGN-UP-BY-ORGANIZATION-PROTECTED') &&
      errorData.subtype === 'ERROR-USER-NOT-IN-ORGANIZATION';

    return isAuthError;
  }

  /**
   * Clear all ambassador-signup-related cache data
   */
  function clearAmbassadorSignupCache() {
    queryClient.removeQueries({ queryKey: ['ambassador-signup'] });
  }

  return {
    // ===== NEW API HOOKS =====
    // Queries
    usePublicCampaignQuery,
    useProtectedCampaignQuery,
    usePublicTeamspaceQuery,
    useProtectedTeamspaceQuery,

    // Mutations
    useUpdateLanguageMutation,
    useRegisterHelperByEmailMutation,
    useManagePhoneNumberMutation,
    useVerifyPhoneNumberCodeMutation,
    useUpdateNameMutation,
    useUpdateProfilePictureMutation,
    useUpdatePositionLanguagesMutation,
    useUpdateAddressMutation,
    useUpdateActionsInterestedInMutation,
    useFillTouchPointsMutation,

    // ===== UTILITIES =====
    clearAmbassadorSignupCache,
    queryKeys,
  };
}
